import { RUNNER_PHASES, type RaceSnapshot, type RunnerPhase } from '@sprinters/shared';

export interface RunnerView {
  /** Distance parcourue (m). */
  x: number;
  /** Vitesse (m/s). */
  v: number;
  phase: RunnerPhase;
  falls: number;
  /** Temps d'arrivée (ms) une fois la ligne franchie. */
  finishTimeMs: number | null;
}

export interface RaceFrame {
  /** Temps de course interpolé (ms), négatif pendant le décompte. */
  t: number;
  runners: RunnerView[];
}

/** Délai de rendu : on affiche le passé pour toujours disposer de deux snapshots à interpoler. */
export const INTERPOLATION_DELAY_MS = 100;
const OFFSET_WINDOW = 30;
const MAX_BUFFERED = 60;
/** Au-delà de ce retard sans nouveau snapshot, on fige l'affichage plutôt que d'extrapoler. */
const MAX_STALENESS_MS = 300;

const toPhase = (index: number): RunnerPhase => RUNNER_PHASES[index] ?? 'idle';

function toView(raw: RaceSnapshot['runners'][number]): RunnerView {
  return {
    x: raw[0],
    v: raw[1],
    phase: toPhase(raw[2]),
    falls: raw[3],
    finishTimeMs: raw[4] < 0 ? null : raw[4],
  };
}

/**
 * Tampon de snapshots serveur avec interpolation temporelle.
 *
 * L'horloge de course du serveur est rapprochée de l'horloge locale en retenant, parmi les
 * derniers snapshots, celui qui a subi le moins de retard réseau (offset maximal).
 */
export class SnapshotBuffer {
  private snapshots: RaceSnapshot[] = [];
  private offsets: number[] = [];
  private currentHeatId: string | null = null;

  get heatId(): string | null {
    return this.currentHeatId;
  }

  clear(): void {
    this.snapshots = [];
    this.offsets = [];
    this.currentHeatId = null;
  }

  push(snapshot: RaceSnapshot, nowMs: number): void {
    if (snapshot.heatId !== this.currentHeatId) {
      this.clear();
      this.currentHeatId = snapshot.heatId;
    }
    const last = this.snapshots.at(-1);
    if (last && snapshot.t <= last.t) return;

    this.snapshots.push(snapshot);
    if (this.snapshots.length > MAX_BUFFERED) this.snapshots.shift();
    this.offsets.push(snapshot.t - nowMs);
    if (this.offsets.length > OFFSET_WINDOW) this.offsets.shift();
  }

  /** Dernière position connue de chaque coureur, sans interpolation (écran de résultats). */
  latest(): RaceFrame | null {
    const last = this.snapshots.at(-1);
    return last ? { t: last.t, runners: last.runners.map(toView) } : null;
  }

  sample(nowMs: number): RaceFrame | null {
    const last = this.snapshots.at(-1);
    const first = this.snapshots[0];
    if (!last || !first || this.offsets.length === 0) return null;

    const offset = Math.max(...this.offsets);
    const renderT = Math.min(nowMs + offset - INTERPOLATION_DELAY_MS, last.t + MAX_STALENESS_MS);
    if (renderT >= last.t) return { t: last.t, runners: last.runners.map(toView) };
    if (renderT <= first.t) return { t: first.t, runners: first.runners.map(toView) };

    let index = this.snapshots.length - 2;
    while (index > 0 && (this.snapshots[index] as RaceSnapshot).t > renderT) index--;
    const a = this.snapshots[index] as RaceSnapshot;
    const b = this.snapshots[index + 1] as RaceSnapshot;
    const alpha = (renderT - a.t) / (b.t - a.t);

    return {
      t: renderT,
      runners: a.runners.map((from, lane) => {
        const to = b.runners[lane] ?? from;
        const source = alpha < 0.5 ? from : to;
        return {
          x: from[0] + (to[0] - from[0]) * alpha,
          v: from[1] + (to[1] - from[1]) * alpha,
          phase: toPhase(source[2]),
          falls: source[3],
          finishTimeMs: source[4] < 0 ? null : source[4],
        };
      }),
    };
  }
}
